import React, { useEffect, useState } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { CircularProgress, Typography } from '@chr/eds-react';
import Grid from '@mui/material/Grid';
import './ShipmentDetails.scss';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { ShipmentDetailsHeader } from './ShipmentDetailsHeader';
import { useShipment } from '../../common/hooks/useShipment';
import ShipmentDetailsTabs from './ShipmentDetailsTabs';
import { ShipmentDetailsInfo } from './ShipmentDetailsInfo';
import { CargoSummary } from './CargoSummary';
import { useTranslation } from '@chr/online-shared-ui-localizations';
import { FaqModal } from '../../common/components/faq/FaqModal';
import { TurnstileCaptchaComponent } from '../../common/components/turnstile-captcha/turnstile-captcha';
import { ContainerSummary } from '../containerDetails/ContainerSummary';
import { JobType, ShippingMode, Status } from '../../common/apis/dtos';
import { NotFound } from '../../common/components/not-found/NotFound';
import { useAnalyticsPageView } from '../../common/hooks/useAnalyticsPageView';
import { useAdobeActions } from '../../common/hooks/useAnalyticsUIEvents';
import { useChrLdFlag } from '@chr/common-launchdarkly';

interface ShipmentDetailsProps {
  shipmentNumber: string;
}

export const ShipmentDetails = () => {
  const { shipmentNumber } = useParams<ShipmentDetailsProps>();
  const location = useLocation();
  const postalCode = new URLSearchParams(location.search).get('postalCode') || undefined;
  const enhancedSearch = useChrLdFlag('enhanced-externaltracking-search', false);
  const isReferenceSearch = !!postalCode;
  const referenceNumber = isReferenceSearch ? shipmentNumber : undefined;
  const [captchaToken, setCaptchaToken] = useState<string | undefined>();
  const { shipment, isLoading } = useShipment(
    isReferenceSearch ? undefined! : shipmentNumber,
    isReferenceSearch ? referenceNumber : undefined,
    isReferenceSearch ? postalCode : undefined,
    captchaToken
  );
  const translate = useTranslation();
  const { breakpoints } = useTheme();
  const isMobileScreen = useMediaQuery(breakpoints.down('md'));
  const [showFaqModal, setShowFaqModal] = useState(false);
  const { handleAdobeAction } = useAdobeActions();
  const history = useHistory();
  const isEnabled = enhancedSearch && !captchaToken;
  const [isWaitingForCaptchaFetch, setIsWaitingForCaptchaFetch] = useState(false);
  const [hasCaptchaFetchStarted, setHasCaptchaFetchStarted] = useState(false);
  // Bumping this counter forces the Turnstile widget to issue a fresh,
  // unconsumed token (single-use tokens cause 409 from the gateway when reused).
  const [captchaResetTracker, setCaptchaResetTracker] = useState(0);
 
  useAnalyticsPageView({ "pageName": "Shipment Detail Page", "pagePath": "/detail", "siteName": "Online.ExternalTracking.UI" });

  useEffect(() => {
    setCaptchaToken(undefined);
    setIsWaitingForCaptchaFetch(false);
    setHasCaptchaFetchStarted(false);
    setCaptchaResetTracker((n) => n + 1);
  }, [shipmentNumber, postalCode]);

  useEffect(() => {
    if (!isWaitingForCaptchaFetch) {
      return;
    }

    if (isLoading) {
      setHasCaptchaFetchStarted(true);
      return;
    }

    if (hasCaptchaFetchStarted) {
      setIsWaitingForCaptchaFetch(false);
      setHasCaptchaFetchStarted(false);
    }
  }, [hasCaptchaFetchStarted, isLoading, isWaitingForCaptchaFetch]);

  const onNeedHelp = () => {
    handleAdobeAction('button', 'click', 'Need Help(details)');
    setShowFaqModal(true);
  };

  const handleCaptchaSuccess = (token: string) => {
    setIsWaitingForCaptchaFetch(true);
    setHasCaptchaFetchStarted(false);
    setCaptchaToken(token);
  };

  const renderLoadingState = () => {
    return (
      <Box
        sx={{
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          background: 'white',
          padding: '100px 24px',
          ...(isMobileScreen ? {} : { height: 'calc(100% - 440px)' }),
        }}
      >
        { enhancedSearch && isEnabled ?
            <TurnstileCaptchaComponent                
              isEnabled={isEnabled}
              resetTracker={captchaResetTracker}
              onSuccessCallback={handleCaptchaSuccess}
            />
            :
            <CircularProgress size="xl" aria-label="Progress Icon" />
        }          
      </Box>        
    );
  };

  const renderShipmentDetails = () => {
    if (shipment.chrTrackingNumber) {
      return (
        <>
          <Grid
            className="shipment-details-section"
            container
            data-testid="shipment-details-grid"
            spacing={0}
            sx={{
              padding: isMobileScreen
                ? '30px 20px 20px 20px'
                : '30px 48px 15px 48px',
              width: '100%',
              background: 'white',
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h1">
                {translate(shipment.jobType === JobType.Order ? 'Order Details' : 'Shipment Details')}
              </Typography>
            </Grid>
            {isMobileScreen ? (
              <Box
                sx={{
                  border: '1px solid #E5E6E7',
                  width: '100%',
                }}
              >
                <ShipmentDetailsInfo
                  shipment={shipment}
                  isMobileScreen={isMobileScreen}
                />
              </Box>
            ) : (
              <ShipmentDetailsInfo
                shipment={shipment}
                isMobileScreen={isMobileScreen}
              />
            )}
            {shipment.mode == ShippingMode.Air && shipment.cargoSummary && (
              <CargoSummary
                shipment={shipment}
                isMobileScreen={isMobileScreen}
              />
            )}
          </Grid>
          {shipment.containers.length > 0 && (
            <Box
              sx={{
                padding: isMobileScreen ? '20px' : '15px 48px 45px 48px',
                background: 'white',
              }}
            >
              <ContainerSummary
                data-testid="container-summary"
                containers={shipment.containers}
                isMobileScreen={isMobileScreen}
              />
            </Box>
          )}
          <Box
            sx={{
              padding: isMobileScreen ? '20px' : '15px 48px 45px 48px',
              background: 'white',
            }}
          >
            <ShipmentDetailsTabs
              shipment={shipment}
              isMobileScreen={isMobileScreen}
            />
          </Box>
        </>
      );
    } else {
      return <NotFound onCheckFAQ={onNeedHelp} />;
    }
  };

  const renderContent = () => {
    if (isLoading || isEnabled || isWaitingForCaptchaFetch) {
      return renderLoadingState();
    } else {
      return renderShipmentDetails();
    }
  };

  return (
    <>
      <ShipmentDetailsHeader
        onTrack={(trackingNumber: string) => {
          history.push(`/detail/${encodeURIComponent(trackingNumber)}`);
        }}
        {...(enhancedSearch ? {
          onTrackReference: (ref: string, zip: string) => {
            history.push(`/detail/${encodeURIComponent(ref)}?postalCode=${encodeURIComponent(zip)}`);
          },
          referenceNumber,
          postalCode,
        } : {})}
        trackingNumber={isReferenceSearch || postalCode ? undefined : (shipmentNumber || undefined)}
      />
      {renderContent()}
      <FaqModal
        open={showFaqModal}
        handleClose={() => {
          setShowFaqModal(false);
        }}
      />
    </>
  );
};

